import { ERROR_CODES, type ErrorResponse, SIGN_IN_ERRORS } from '@repo/contracts';
import axios, { AxiosError, type AxiosInstance } from 'axios';
import { toast } from 'sonner';

import { REFRESH_LOCK_NAME } from '@/constants/auth';
import { authKeys } from '@/hooks/auth/query-keys';
import { queryClient } from '@/lib/query-client';
import { readCookie } from '@/shared/utils/read-cookie';

import { redirectToLogin, redirectToSignIn } from './redirect-to-sign-in';
import { requestIdOf } from './request-id';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** The sign-in page's own session probe: a 401 there is an answer, not a reason to redirect. */
    skipSignInRedirect?: boolean;
    csrfRetried?: boolean;
    refreshed?: boolean;
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

const CSRF_COOKIE = 'cyrino_csrf';
const CSRF_HEADER = 'x-csrf-token';
const REFRESH_URL = '/auth/refresh';
const CURRENT_USER_URL = '/me';

/** The session is an httpOnly cookie, so nothing here reads or stores a token. */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  // The gateway reads a list as repeated keys (`tags=a&tags=b`), not axios's default `tags[]=a`.
  paramsSerializer: { indexes: null },
});

apiClient.interceptors.request.use((config) => {
  const token = readCookie(CSRF_COOKIE);

  if (token) config.headers[CSRF_HEADER] = token;

  return config;
});

let refreshInFlight: Promise<void> | null = null;

// Tabs and both apps share one cookie jar, so two refreshes at once would
// spend the same refresh token. The lock queues them, and the second then
// sends the cookie the first one set. Absent on plain HTTP, where the server's
// reuse interval covers the race instead.
const withRefreshLock = async (task: () => Promise<void>): Promise<void> => {
  if (typeof navigator === 'undefined' || !navigator.locks) return task();

  await navigator.locks.request(REFRESH_LOCK_NAME, task);
};

const refreshSession = (): Promise<void> => {
  refreshInFlight ??= withRefreshLock(async () => {
    await apiClient.post(REFRESH_URL);
  }).finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

const failedRefresh = (refreshError: unknown, skipRedirect: boolean): Promise<never> => {
  const response =
    refreshError instanceof AxiosError
      ? (refreshError as AxiosError<ErrorResponse>).response
      : undefined;
  const status = response?.status;
  const code = response?.data?.code;

  // Only a 401 from refresh says the session is gone. A 5xx or a dropped
  // connection says nothing about it, and must not cost anyone their page.
  if (status === 401 && !skipRedirect) {
    if (code === ERROR_CODES.accountDisabled) {
      redirectToLogin(`refresh refused: ${code}`, SIGN_IN_ERRORS.accountDisabled);
    } else {
      redirectToSignIn(`refresh refused: ${code ?? 'no code'}`);
    }
  }

  return Promise.reject(refreshError);
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ErrorResponse>) => {
    const request = error.config;
    const status = error.response?.status;
    const code = error.response?.data?.code;

    // The CSRF cookie is replaced by a refresh, so a write in flight can carry
    // the old value. The request interceptor re-reads the cookie on the retry.
    if (request && status === 403 && code === ERROR_CODES.csrfMismatch && !request.csrfRetried) {
      request.csrfRetried = true;
      return apiClient.request(request);
    }

    // A refused request is otherwise silent: no page renders a query error, so
    // the screen simply stays empty and reads as a broken application rather
    // than as missing access. One toast at a time, because a single screen can
    // fire several requests that are all refused for the same reason.
    if (status === 403) {
      const requestId = requestIdOf(error);
      const message = error.response?.data?.message;

      toast.error('You do not have access to this', {
        id: 'forbidden',
        description: [message, requestId ? `Reference: ${requestId}` : null]
          .filter(Boolean)
          .join(' · '),
      });
    }

    const shouldRefresh =
      status === 401 && request && !request.refreshed && request.url !== REFRESH_URL;

    if (!shouldRefresh) return Promise.reject(error);

    try {
      await refreshSession();
    } catch (refreshError) {
      return failedRefresh(refreshError, Boolean(request.skipSignInRedirect));
    }

    // The request that triggered the refresh is already waiting on the user,
    // and refetching it twice would only cancel the retry below.
    if (request.url !== CURRENT_USER_URL) {
      void queryClient.invalidateQueries(
        { queryKey: authKeys.currentUser() },
        { cancelRefetch: false },
      );
    }

    request.refreshed = true;
    return apiClient.request(request);
  },
);

export const extractErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    return error.response?.data?.message ?? error.message;
  }

  return error instanceof Error ? error.message : 'Something went wrong';
};
